// src/components/layouts/app/sub/HeaderDesktop.tsx
import React from "react";
import { Link } from "react-router-dom";
import { Star, Shield, Package, Headphones, Smartphone, Store } from "lucide-react";
import { HeaderProps, CategoryItem } from "../types";
import SearchBar from "./SearchBar";
import UserMenu from "../UserMenu";
import CategoryDropdown from "./CategoryDropdown";
import { UnderlineLink } from "../../../../../ui/elements/UnderlineLink";

interface HeaderDesktopProps extends HeaderProps {
  categories: CategoryItem[];
  mainCategories: string[];
  handleSearch: (e: React.FormEvent) => void;
  getCategorySlug: (cat: string) => string;
  onCategorySelect?: (category: CategoryItem) => void;
}

const HeaderDesktop: React.FC<HeaderDesktopProps> = ({
  categories,
  handleSearch,
  onCategorySelect,
  scrolled,
  searchQuery,
  setSearchQuery,
  isUserMenuOpen,
  setIsUserMenuOpen,
  isCategoryDropdownOpen,
  setIsCategoryDropdownOpen,
}) => {
  const secondLineMenuFirstGroup = [
    { name: "Sélections", path: "/recommended", icon: Star, color: "text-amber-600", hoverColor: "hover:text-amber-700", borderColor: "bg-amber-500" },
    { name: "Protection", path: "/order-protection", icon: Shield, color: "text-green-600", hoverColor: "hover:text-green-700", borderColor: "bg-green-500" },
    { name: "Acheteurs", path: "/buyer-center", icon: Package, color: "text-blue-600", hoverColor: "hover:text-blue-700", borderColor: "bg-blue-500" },
  ];

  const secondLineMenuSecondGroup = [
    { name: "Assistance", path: "/help-center", icon: Headphones, color: "text-purple-600", hoverColor: "hover:text-purple-700", borderColor: "bg-purple-500" },
    { name: "Application", path: "/app", icon: Smartphone, color: "text-indigo-600", hoverColor: "hover:text-indigo-700", borderColor: "bg-purple-500" },
    { name: "Vendre sur kayovex.com", path: "/become-seller", icon: Store, color: "text-orange-600", hoverColor: "hover:text-orange-700", borderColor: "bg-orange-500", isFeatured: true },
  ];

  return (
    <header className="hidden lg:block sticky top-0 z-50 bg-white border-b shadow-sm">
      <div className="container mx-auto px-4">
        {/* Première ligne - fixe */}
        <div className="flex items-center justify-between h-14 py-1">
          <Link to="/" className="flex items-center gap-1.5">
            <img src="/assets/logo/notextfavicon.jpeg" alt="Kayovex" className="w-10 object-contain" />
            <div className="flex flex-col leading-tight">
              <span className="font-bold text-gray-900 text-lg">Kayovex</span>
              <span className="text-gray-500 text-[10px]">Marketplace</span>
            </div>
          </Link>

          <div className="flex items-center gap-2 ml-4 pl-4 border-l border-gray-200">
            <div className="w-8 h-6 rounded-sm overflow-hidden border border-gray-200">
              <img src="https://flagpedia.net/data/flags/w160/bj.png" alt="Bénin" className="w-full h-full object-cover" />
            </div>
            <div className="leading-tight">
              <div className="text-sm font-medium text-gray-900">Bénin</div>
              <div className="text-xs text-gray-500">Livraison gratuite dès 50 €</div>
            </div>
          </div>

          <div className="flex flex-1 max-w-lg mx-4">
            <SearchBar
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              handleSearch={handleSearch}
              placeholder="Rechercher produits, marques..."
              scrolled={false}
            />
          </div>

          <UserMenu
            isUserMenuOpen={isUserMenuOpen}
            setIsUserMenuOpen={setIsUserMenuOpen}
            scrolled={false}
          />
        </div>

        {/* Deuxième ligne - disparition au scroll */}
        <div
          className={`
            border-t border-gray-100  transition-all duration-700 ease-in-out
            ${scrolled
              ? "max-h-0 opacity-0 -mt-1 py-0 invisible"
              : "max-h-32 opacity-100 py-1.5 visible"
            }
          `}
        >
          <div className="flex items-center justify-between w-full">
            {/* Groupe gauche */}
            <div className="flex items-center gap-4">
              <CategoryDropdown
                categories={categories}
                isCategoryDropdownOpen={isCategoryDropdownOpen}
                setIsCategoryDropdownOpen={setIsCategoryDropdownOpen}
                onCategorySelect={onCategorySelect}
              />
              <div className="flex items-center gap-4 ml-3">
                {secondLineMenuFirstGroup.map((item) => (
                  <UnderlineLink
                    key={item.name}
                    to={item.path}
                    activeColor={item.borderColor}
                    inactiveColor={item.borderColor}
                    className="flex items-center gap-1.5"
                  >
                    <item.icon className={`w-4 h-4 ${item.color} group-hover:scale-105 transition-transform`} />
                    <span className={`text-sm font-medium text-gray-700 group-hover:text-gray-900 ${item.hoverColor}`}>
                      {item.name}
                    </span>
                  </UnderlineLink>
                ))}
              </div>
            </div>

            {/* Groupe droit */}
            <div className="flex items-center gap-4">
              {secondLineMenuSecondGroup.map((item) => (
                <UnderlineLink
                  key={item.name}
                  to={item.path}
                  activeColor={item.borderColor}
                  inactiveColor={item.borderColor}
                  className="flex items-center gap-1.5 relative"
                >
                  <item.icon className={`w-4 h-4 ${item.color} group-hover:scale-105 transition-transform`} />
                  <span className={`text-sm font-medium ${item.isFeatured ? "text-orange-700" : "text-gray-700"} group-hover:${item.isFeatured ? "text-orange-800" : "text-gray-900"}`}>
                    {item.name}
                  </span>
                  {item.isFeatured && !scrolled && (
                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-orange-500 rounded-full animate-pulse" />
                  )}
                </UnderlineLink>
              ))}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default HeaderDesktop;